import React, { useContext, useMemo } from 'react';
import { useSession } from '@clerk/clerk-react';
import { createSupabaseClientWithSession } from '@/integrations/supabase/client';
import { authProviderName, AuthProviderName } from '@/config/auth';
import { lazy, Suspense } from 'react';
import { SupabaseClientContext } from './supabase-client-context';

function ClerkSupabaseClientProvider({ children }: { children: React.ReactNode }) {
  const { session } = useSession();

  const supabase = useMemo(() => {
    console.log('[SupabaseClientProvider] Creating Supabase client with Clerk session:', !!session);

    return createSupabaseClientWithSession(
      session
        ? async () => {
            try {
              // Get the default JWT token from Clerk
              // The template system has been deprecated, just use the default token
              const token = await session.getToken();
              if (!token) {
                console.warn('[SupabaseClientProvider] Clerk returned empty token');
              }
              return token ?? null;
            } catch (error) {
              console.warn('[SupabaseClientProvider] Failed to obtain Clerk session token for Supabase', error);
              return null;
            }
          }
        : null
    );
  }, [session]);

  return (
    <SupabaseClientContext.Provider value={{ supabase }}>
      {children}
    </SupabaseClientContext.Provider>
  );
}

const Auth0SupabaseClientProvider = lazy(() =>
  import('./Auth0SupabaseClientProvider').then((module) => ({
    default: module.Auth0SupabaseClientProvider,
  })),
);

export function SupabaseClientProvider({
  children,
  providerName = authProviderName,
}: {
  children: React.ReactNode;
  providerName?: AuthProviderName;
}) {
  if (providerName === 'auth0') {
    return (
      <Suspense fallback={null}>
        <Auth0SupabaseClientProvider>{children}</Auth0SupabaseClientProvider>
      </Suspense>
    );
  }
  return (
    <ClerkSupabaseClientProvider>{children}</ClerkSupabaseClientProvider>
  );
}

/** Returns the provider-authenticated Supabase client used by feature code. */
export function useSupabase() {
  const context = useContext(SupabaseClientContext);
  if (!context) {
    throw new Error('useSupabase must be used within SupabaseClientProvider');
  }
  return context.supabase;
}
